/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState, type PropsWithChildren } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { fg } from '@atlaskit/platform-feature-flags';
import { Bleed, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';
import { getProductIcon, type ActionCardProduct } from './product-icon';

const styles = cssMap({
	actionCardWrapper: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.xlarge'),
	},
	actionCardWrapperBorder: {
		borderRadius: token('radius.xlarge'),
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	actionCardHeader: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	actionCardHeaderCollapsed: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	actionCardContentWrapper: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	actionCardFooter: {
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	actionCardFooterOuterMargin: {
		marginTop: token('space.150'),
		marginBottom: token('space.negative.150'),
	},
	titleBold: {
		font: token('font.heading.xsmall'),
	},
});

const getChevronIcon = (isExpanded: boolean, invertChevron: boolean) => {
	if (invertChevron) {
		return isExpanded ? ChevronDownIcon : ChevronUpIcon;
	}
	return isExpanded ? ChevronUpIcon : ChevronDownIcon;
};

const renderTitle = (title: string, isTitleBold: boolean) => {
	if (isTitleBold) {
		return (
			<Box as="span" xcss={styles.titleBold}>
				{title}
			</Box>
		);
	}
	return <Text>{title}</Text>;
};

export const ActionCardWrapper = ({
	title,
	subtitle,
	product,
	children,
	isExpandable = false,
	defaultExpanded = true,
	isTitleBold = false,
	headerIcon,
	headerActionIcon,
	invertChevron = false,
}: {
	title?: string;
	subtitle?: React.ReactNode;
	product?: ActionCardProduct;
	children: React.ReactNode;
	isExpandable?: boolean;
	defaultExpanded?: boolean;
	isTitleBold?: boolean;
	headerIcon?: React.ReactNode;
	headerActionIcon?: React.ReactNode;
	invertChevron?: boolean;
}): JSX.Element => {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const { formatMessage } = useIntl();

	const shouldShowContent = !isExpandable || isExpanded;
	const showSubtitle = Boolean(
		subtitle && (fg('ai_features_in_icp_m1') || fg('rovo_chat_enable_tasks')),
	);

	return (
		<Box
			xcss={
				fg('rovo_chat_enable_tasks') ? styles.actionCardWrapper : styles.actionCardWrapperBorder
			}
			testId="action-card-wrapper"
		>
			{title && (
				<Box
					xcss={
						!shouldShowContent && fg('rovo_chat_enable_tasks')
							? styles.actionCardHeaderCollapsed
							: styles.actionCardHeader
					}
				>
					<Inline space="space.100" alignBlock="center" spread="space-between">
						<Inline space="space.100" alignBlock="center">
							{headerIcon ?? getProductIcon(product)}
							{showSubtitle ? (
								<Stack>
									{renderTitle(title, isTitleBold)}
									<Text size="small" color="color.text.subtlest">
										{subtitle}
									</Text>
								</Stack>
							) : (
								renderTitle(title, isTitleBold)
							)}
						</Inline>
						{isExpandable && (
							<Inline space="space.050" alignBlock="center">
								{isExpanded && headerActionIcon}
								<IconButton
									appearance="subtle"
									icon={getChevronIcon(isExpanded, invertChevron)}
									label={
										isExpanded ? formatMessage(messages.collapse) : formatMessage(messages.expand)
									}
									onClick={() => setIsExpanded((isExpanded) => !isExpanded)}
								/>
							</Inline>
						)}
					</Inline>
				</Box>
			)}

			{shouldShowContent && <Box xcss={styles.actionCardContentWrapper}>{children}</Box>}
		</Box>
	);
};

export const ActionCardFooter = ({
	children,
	testId,
}: PropsWithChildren<{ testId?: string }>): JSX.Element => {
	return (
		<Bleed inline="space.150" xcss={styles.actionCardFooterOuterMargin}>
			<Box xcss={styles.actionCardFooter} testId={testId}>
				{children}
			</Box>
		</Bleed>
	);
};
